import { STATUS } from '../../utils/constants';
import { buildQueryKey } from '../../utils/helpers';
import {
  addToDictionary,
  updateList,
  changeListStatus,
  resetState,
} from '../helpers';
import DEFAULT_REQUEST from '../constants';
import {
  CREATE_INTERNAL_REQUEST_TYPES,
  DELETE_INTERNAL_REQUEST_TYPES,
  FIND_INTERNAL_REQUEST_TYPES,
  GET_INTERNAL_REQUESTS_TYPES,
  UPDATE_INTERNAL_REQUEST_TYPES,
  SUBMIT_INTERNAL_REQUEST_TYPES,
  APPROVE_INTERNAL_REQUEST_TYPES,
  REJECT_INTERNAL_REQUEST_TYPES,
  GET_ADMIN_QUEUE_TYPES,
  GET_PENDING_CLOSE_QUEUE_TYPES,
} from './constants';
import Pager from '../../api/helpers/Pager';

/**
 *
 * @type {{list: {default: {}, pagination: {total: number, pageNumber: null, pageSize: null}, meta: {currentKey: string}}, dictionary: {}, adminQueue: {}}}
 */
const initialState = {
  dictionary: {},
  list: {
    meta: {
      currentKey: DEFAULT_REQUEST,
    },
    pagination: {
      pageNumber: null,
      pageSize: null,
      total: 0,
    },
    [DEFAULT_REQUEST]: {},
  },
  adminQueue: {
    items: [],
    total: 0,
    status: null,
  },
  pendingCloseQueue: {
    items: [],
    total: 0,
    status: null,
  },
};

export default function internalRequestReducer(state = initialState, action) {
  let queryKey;
  let pager;
  let listStatus;
  let error;
  let id;
  let resourceModel;
  let resetToDefault;

  switch (action.type) {
    case GET_INTERNAL_REQUESTS_TYPES.REQUEST:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;
      resetToDefault = action.payload.resetToDefault;

      if (
        Pager.hasPageSizeChanged(
          state.list.pagination.pageSize,
          pager?.pageSize
        ) ||
        resetToDefault
      ) {
        return resetState(state, initialState, {
          queryKey,
          pager,
        });
      }

      if (
        [STATUS.RELOADING, STATUS.SUCCESS, STATUS.CACHED].includes(listStatus)
      ) {
        return changeListStatus(state, { pager, queryKey }, STATUS.RELOADING);
      }
      return changeListStatus(state, { pager, queryKey }, STATUS.LOADING);
    case GET_INTERNAL_REQUESTS_TYPES.SUCCESS:
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: addToDictionary(
          state.dictionary,
          action.payload.resources,
          resourceModel
        ),
        list: updateList(state.list, action.payload),
      };
    case GET_INTERNAL_REQUESTS_TYPES.ERROR:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      error = action.payload.error;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;

      return changeListStatus(
        state,
        { pager, queryKey, error },
        listStatus === STATUS.RELOADING ? STATUS.CACHED : STATUS.ERROR
      );

    case FIND_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case FIND_INTERNAL_REQUEST_TYPES.SUCCESS:
      const { resource } = action.payload;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [resource.id]: new resourceModel({
            ...state.dictionary[resource.id],
            ...resource,
            findFetched: true,
          }),
        },
      };
    case FIND_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case CREATE_INTERNAL_REQUEST_TYPES.REQUEST:
      return state;
    case CREATE_INTERNAL_REQUEST_TYPES.SUCCESS:
      return state;
    case CREATE_INTERNAL_REQUEST_TYPES.ERROR:
      return state;

    case UPDATE_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case UPDATE_INTERNAL_REQUEST_TYPES.SUCCESS:
      return state;
    case UPDATE_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case SUBMIT_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case SUBMIT_INTERNAL_REQUEST_TYPES.SUCCESS:
      const submitResource = action.payload.data;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [submitResource.id]: new resourceModel({
            ...state.dictionary[submitResource.id],
            ...submitResource,
          }),
        },
      };
    case SUBMIT_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case APPROVE_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case APPROVE_INTERNAL_REQUEST_TYPES.SUCCESS:
      const approveResource = action.payload.data;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [approveResource.id]: new resourceModel({
            ...state.dictionary[approveResource.id],
            ...approveResource,
          }),
        },
      };
    case APPROVE_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case REJECT_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case REJECT_INTERNAL_REQUEST_TYPES.SUCCESS:
      const rejectResource = action.payload.data;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [rejectResource.id]: new resourceModel({
            ...state.dictionary[rejectResource.id],
            ...rejectResource,
          }),
        },
      };
    case REJECT_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case DELETE_INTERNAL_REQUEST_TYPES.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case DELETE_INTERNAL_REQUEST_TYPES.SUCCESS:
      id = action.payload.id;

      const newDictionary = { ...state.dictionary };
      delete newDictionary[id];
      return {
        ...state,
        dictionary: newDictionary,
      };
    case DELETE_INTERNAL_REQUEST_TYPES.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case GET_ADMIN_QUEUE_TYPES.REQUEST:
      return {
        ...state,
        adminQueue: {
          ...state.adminQueue,
          status: STATUS.LOADING,
        },
      };
    case GET_ADMIN_QUEUE_TYPES.SUCCESS:
      resourceModel = action.payload.resourceModel;
      const queueResources = action.payload.resources || [];
      const queueItems = queueResources.map(
        (queueResource) => queueResource.id
      );

      return {
        ...state,
        dictionary: addToDictionary(
          state.dictionary,
          queueResources,
          resourceModel
        ),
        adminQueue: {
          items: queueItems,
          total: action.payload.total || 0,
          status: STATUS.SUCCESS,
        },
      };
    case GET_ADMIN_QUEUE_TYPES.ERROR:
      return {
        ...state,
        adminQueue: {
          ...state.adminQueue,
          status: STATUS.ERROR,
        },
      };

    case GET_PENDING_CLOSE_QUEUE_TYPES.REQUEST:
      return {
        ...state,
        pendingCloseQueue: {
          ...state.pendingCloseQueue,
          status: STATUS.LOADING,
        },
      };
    case GET_PENDING_CLOSE_QUEUE_TYPES.SUCCESS:
      resourceModel = action.payload.resourceModel;
      const pendingCloseResources = action.payload.resources || [];
      const pendingCloseItems = pendingCloseResources.map(
        (pendingCloseResource) => pendingCloseResource.id
      );

      return {
        ...state,
        dictionary: addToDictionary(
          state.dictionary,
          pendingCloseResources,
          resourceModel
        ),
        pendingCloseQueue: {
          items: pendingCloseItems,
          total: action.payload.total || 0,
          status: STATUS.SUCCESS,
        },
      };
    case GET_PENDING_CLOSE_QUEUE_TYPES.ERROR:
      return {
        ...state,
        pendingCloseQueue: {
          ...state.pendingCloseQueue,
          status: STATUS.ERROR,
        },
      };

    default:
      return state;
  }
}
